import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowRight, Radio, Tv2, Play } from "lucide-react";
import { useImages } from "../../lib/ImagesContext";
import { useContent } from "../../lib/ContentContext";

/**
 * Hero — center content + curved TV wall extending beyond the viewport,
 * with a single vertical crimson beam through the centre screen.
 */
const SCREEN_META = [
  { slot: "hero-1", label: "AIRPORT · MUM T2" },
  { slot: "hero-2", label: "MALL · GURUGRAM" },
  { slot: "hero-3", label: "CINEMA · MUMBAI" },
  { slot: "hero-4", label: "XMLOOPS TV · LIVE" },
  { slot: "hero-5", label: "HOTEL · BLR" },
  { slot: "hero-6", label: "STUDIO · DELHI" },
  { slot: "hero-7", label: "F&B · MUMBAI" },
];

const FEATURES_META = [
  { titleKey: "hero.feature_1_title", bodyKey: "hero.feature_1_body", icon: "radio" },
  { titleKey: "hero.feature_2_title", bodyKey: "hero.feature_2_body", icon: "tv" },
  { titleKey: "hero.feature_3_title", bodyKey: "hero.feature_3_body", icon: "play" },
];

export default function Hero() {
  const ref = useRef(null);
  const { getImg } = useImages();
  const { getText } = useContent();
  const SCREENS = SCREEN_META.map((m) => ({ label: m.label, img: getImg(m.slot) }));
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yWall = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const half = (SCREENS.length - 1) / 2;

  // Rotating "on air" card — cycles every 2.4s
  const [activeIdx, setActiveIdx] = useState(half);
  useEffect(() => {
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % SCREENS.length);
    }, 2400);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile carousel mode (<768px). Desktop/tablet (>=768px) are untouched.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden pt-28 md:pt-32 pb-16 md:pb-24 bg-warm"
      data-testid="hero-section"
    >
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#e63946] opacity-20 blur-[220px]" />
        <div className="absolute bottom-0 left-0 w-[520px] h-[520px] rounded-full bg-[#a80b1e] opacity-25 blur-[180px]" />
      </div>

      {/* Micro grid */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative">
        {/* CENTERED CONTENT */}
        <motion.div style={{ opacity }} className="mx-auto max-w-[1200px] px-6 md:px-10 text-center flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="tag-glass"
            data-testid="hero-overline"
          >
            <span className="dot" /> {getText("hero.tag")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-white mt-6 leading-[1.02] tracking-tight
                       text-[10vw] sm:text-[7.5vw] lg:text-[4.8vw] max-w-[1000px]"
            data-testid="hero-heading"
          >
            {getText("hero.headline_1a")}<span className="italic-accent text-white/85">{getText("hero.headline_1b")}</span>{getText("hero.headline_1c")}
            <br />
            {getText("hero.headline_2a")}<span className="italic-accent text-grad-crimson">{getText("hero.headline_2b")}</span>{getText("hero.headline_2c")}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-6 max-w-xl text-white/72 text-base md:text-lg leading-relaxed"
            data-testid="hero-subheading"
          >
            {getText("hero.subtitle")}
          </motion.p>

          {/* CTA with glow beneath */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="relative mt-9 flex flex-wrap items-center justify-center gap-3"
          >
            {/* Glow ring */}
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-8 w-64 h-6 rounded-full bg-[#e63946] opacity-50 blur-2xl" />
            <a href={getText("hero.cta_primary_url")} className="relative btn btn-primary" data-testid="hero-cta-primary">
              {getText("hero.cta_primary_label")}
              <ArrowRight className="w-4 h-4" />
            </a>
            <a href={getText("hero.cta_secondary_url")} className="relative btn btn-glass" data-testid="hero-cta-secondary">
              <Play className="w-3.5 h-3.5" />
              {getText("hero.cta_secondary_label")}
            </a>
          </motion.div>
        </motion.div>

        {/* CURVED TV WALL — extends beyond viewport */}
        <motion.div
          style={{ y: yWall }}
          className="relative mt-16 md:mt-20 h-[420px] md:h-[600px]"
          data-testid="hero-tv-wall"
        >
          {/* Central vertical beam */}
          <div
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-4 md:-top-8 bottom-0 w-[3px]"
            style={{
              background: "linear-gradient(to bottom, transparent 0%, #ff5865 15%, #ffb3b3 45%, #ff5865 70%, transparent 100%)",
              filter: "blur(0.3px)",
              boxShadow: "0 0 24px #ff5865, 0 0 60px #e63946",
            }}
          />
          {/* Beam halo (wider soft glow) */}
          <div
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-8 bottom-0 w-32 opacity-70"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(230,57,70,0.55), rgba(230,57,70,0) 70%)",
              filter: "blur(24px)",
            }}
          />

          <div
            className="absolute inset-x-0 bottom-0 flex justify-center items-end"
            style={{ perspective: "1600px", perspectiveOrigin: "50% 100%" }}
          >
            {SCREENS.map((s, i) => {
              const offset = i - half;
              const rotY = isMobile ? 0 : offset * -16;
              const translateX = isMobile ? 0 : offset * 244;
              const translateY = isMobile ? 0 : Math.abs(offset) * 4;
              const z = 30 - Math.abs(offset);
              const isActive = i === activeIdx;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 80, x: translateX, rotateY: 0 }}
                  animate={{
                    opacity: isMobile ? (isActive ? 1 : 0) : 1,
                    y: isActive ? translateY - 14 : translateY,
                    x: translateX,
                    rotateY: rotY,
                    scale: isActive ? 1.05 : 1,
                  }}
                  transition={{
                    duration: isActive ? 1.1 : 0.9,
                    delay: isMobile ? 0 : 0.12 * Math.abs(offset) + 0.5,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  style={{
                    zIndex: isActive ? 40 : isMobile ? 0 : z,
                    transformStyle: "preserve-3d",
                    transformOrigin: "center bottom",
                    pointerEvents: isMobile && !isActive ? "none" : "auto",
                  }}
                  className="absolute bottom-0 w-[260px] md:w-[300px] h-[370px] md:h-[430px] rounded-2xl overflow-hidden"
                  data-testid={`hero-screen-${i}`}
                >
                  {/* Glass card body */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden">
                    <img src={s.img} alt={s.label} className="w-full h-full object-cover" draggable={false} />

                    {/* Glass sheen */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(160deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 40%, rgba(0,0,0,0.25) 100%)",
                      }}
                    />
                    {/* Soft vignette */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "radial-gradient(ellipse at 50% 30%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
                      }}
                    />

                    {/* Active card crimson tint + glow */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      animate={{ opacity: isActive ? 1 : 0 }}
                      transition={{ duration: 0.5 }}
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(230,57,70,0.28), transparent 55%)",
                        boxShadow: isActive ? "inset 0 0 60px rgba(230,57,70,0.35)" : "none",
                      }}
                    />
                  </div>

                  {/* Frameless glass border (very subtle, no hard line) */}
                  <div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.06))",
                      WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      padding: "1px",
                    }}
                  />

                  {/* Label above screen */}
                  <motion.div
                    className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap"
                    animate={{ opacity: isActive ? 1 : 0.55, y: isActive ? -2 : 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <span
                      className={`font-mono text-[9px] md:text-[10px] uppercase tracking-[0.24em] ${
                        isActive ? "text-[#ff5865]" : "text-white/70"
                      }`}
                    >
                      {s.label}
                    </span>
                  </motion.div>

                  {/* On-air indicator — moves with active */}
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4 }}
                      className="absolute top-2.5 left-2.5 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ff5865] animate-pulse shadow-[0_0_8px_#ff5865]" />
                      <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-white">On air</span>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Mobile-only carousel dots */}
          <div
            className="md:hidden absolute left-1/2 -translate-x-1/2 bottom-3 z-50 flex items-center gap-2"
            data-testid="hero-carousel-dots"
          >
            {SCREENS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                aria-label={`Show screen ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeIdx ? "w-5 bg-[#ff5865]" : "w-1.5 bg-white/40"
                }`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>

          {/* Reflective floor */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-24"
            style={{
              background: "linear-gradient(to bottom, rgba(230,57,70,0.09), transparent 60%)",
            }}
          />
        </motion.div>

        {/* THREE FEATURE COLUMNS below the arc */}
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 mt-12 md:mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 text-center">
            {FEATURES_META.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: 0.08 * i }}
                className="px-2"
                data-testid={`hero-feature-${i}`}
              >
                <div className="flex justify-center mb-4">
                  <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#ff5865]">
                    {f.icon === "radio" ? <Radio className="w-4 h-4" /> : f.icon === "tv" ? <Tv2 className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
                  </div>
                </div>
                <h3 className="font-display text-white text-lg md:text-xl tracking-tight leading-snug">
                  {getText(f.titleKey)}
                </h3>
                <p className="mt-3 text-white/60 text-sm md:text-[15px] leading-relaxed max-w-[320px] mx-auto">
                  {getText(f.bodyKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
